import {
  GET_ALL_PUBLISHED_PROGRAMS,
  GET_PROGRAMS_BY_MONTH,
  SET_AVAIL_DATE,
  SET_BLOCK_DATE,
  GET_HOLIDAYS_BY_MONTH,
  ADD_HOLIDAY,
  REMOVE_HOLIDAY,
} from "../types";
import { call, put, takeLatest } from "redux-saga/effects";
import {
  GetAllPublishedProgramsApi,
  GetProgramsByMonthApi,
  SetAvailDateApi,
  SetBlockDateApi,
} from "../api/programs-api";
import {
  GetHolidaysByMonthApi,
  AddHolidayApi,
  RemoveHolidayApi,
} from "../api/calendar-api";
import { monthKey } from "../reducers/calendarReducer";

// The API answers HTTP 200 even when a request failed — a missing route or a
// rejected payload comes back as `{ status: false, message, data: null }`.
// Coalescing that `null` to `[]` (as this file used to) made a failed month
// load indistinguishable from a month with nothing in it. Throwing instead
// routes it into each worker's existing catch, which dispatches _FAILURE and
// calls onError with a message the UI can show.
const unwrap = (res: any) => {
  const body = res?.data ?? res;
  if (body?.status === false) {
    throw Object.assign(new Error(body?.message || "Request failed"), {
      data: body,
    });
  }
  return body;
};

export function* getAllPublishedProgramsWorker(action: any): any {
  yield put({ type: `${GET_ALL_PUBLISHED_PROGRAMS}_REQUEST` });
  try {
    const res: any = yield call(GetAllPublishedProgramsApi);
    const raw = unwrap(res);
    const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
    yield put({ type: `${GET_ALL_PUBLISHED_PROGRAMS}_SUCCESS`, payload: list });
    if (action.onSuccess) yield call(action.onSuccess, list);
  } catch (error: any) {
    const err = error?.response?.data ?? error;
    yield put({ type: `${GET_ALL_PUBLISHED_PROGRAMS}_FAILURE`, payload: err });
    if (action.onError) yield call(action.onError, err);
  }
}

export function* getProgramsByMonthWorker(action: any): any {
  const { year, month } = action.data ?? {};
  const mk = year != null && month != null ? monthKey(year, month) : "";
  yield put({ type: `${GET_PROGRAMS_BY_MONTH}_REQUEST` });
  try {
    const res: any = yield call(GetProgramsByMonthApi, year, month);
    const raw = unwrap(res);
    // adminCalendar nests the days under `data.days`. The older
    // getProgramsByMonth shapes are still accepted so a rollback on the server
    // does not blank the calendar.
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.days)
        ? raw.days
        : Array.isArray(raw?.data?.days)
          ? raw.data.days
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
    yield put({
      type: `${GET_PROGRAMS_BY_MONTH}_SUCCESS`,
      payload: list,
      meta: { monthKey: mk },
    });
    if (action.onSuccess) yield call(action.onSuccess, list);
  } catch (error: any) {
    const err = error?.response?.data ?? error;
    yield put({ type: `${GET_PROGRAMS_BY_MONTH}_FAILURE`, payload: err });
    if (action.onError) yield call(action.onError, err);
  }
}

export function* setAvailDateWorker(action: any): any {
  yield put({ type: `${SET_AVAIL_DATE}_REQUEST` });
  try {
    unwrap(yield call(SetAvailDateApi, action.data));
    yield put({ type: `${SET_AVAIL_DATE}_SUCCESS` });
    if (action.onSuccess) yield call(action.onSuccess);
  } catch (error: any) {
    const err = error?.response?.data ?? error;
    yield put({ type: `${SET_AVAIL_DATE}_FAILURE`, payload: err });
    if (action.onError) yield call(action.onError, err);
  }
}

export function* setBlockDateWorker(action: any): any {
  yield put({ type: `${SET_BLOCK_DATE}_REQUEST` });
  try {
    unwrap(yield call(SetBlockDateApi, action.data));
    yield put({
      type: `${SET_BLOCK_DATE}_SUCCESS`,
      meta: action.meta,
    });
    if (action.onSuccess) yield call(action.onSuccess);
  } catch (error: any) {
    const err = error?.response?.data ?? error;
    yield put({ type: `${SET_BLOCK_DATE}_FAILURE`, payload: err });
    if (action.onError) yield call(action.onError, err);
  }
}

export function* getAllPublishedProgramsWatcher() {
  yield takeLatest(GET_ALL_PUBLISHED_PROGRAMS, getAllPublishedProgramsWorker);
}

export function* getProgramsByMonthWatcher() {
  yield takeLatest(GET_PROGRAMS_BY_MONTH, getProgramsByMonthWorker);
}

export function* setAvailDateWatcher() {
  yield takeLatest(SET_AVAIL_DATE, setAvailDateWorker);
}

export function* setBlockDateWatcher() {
  yield takeLatest(SET_BLOCK_DATE, setBlockDateWorker);
}

export function* getHolidaysByMonthWorker(action: any): any {
  const { year, month } = action.data ?? {};
  const mk = year != null && month != null ? monthKey(year, month) : "";
  yield put({ type: `${GET_HOLIDAYS_BY_MONTH}_REQUEST` });
  try {
    const res: any = yield call(GetHolidaysByMonthApi, year, month);
    const raw = unwrap(res);
    const list = Array.isArray(raw) ? raw : (raw?.data ?? []);
    yield put({
      type: `${GET_HOLIDAYS_BY_MONTH}_SUCCESS`,
      payload: list,
      meta: { monthKey: mk },
    });
    if (action.onSuccess) yield call(action.onSuccess, list);
  } catch (error: any) {
    const err = error?.response?.data ?? error;
    yield put({ type: `${GET_HOLIDAYS_BY_MONTH}_FAILURE`, payload: err });
    if (action.onError) yield call(action.onError, err);
  }
}

export function* addHolidayWorker(action: any): any {
  yield put({ type: `${ADD_HOLIDAY}_REQUEST` });
  try {
    const res: any = yield call(AddHolidayApi, action.data);
    const payload = unwrap(res) ?? action.data;
    // addHoliday confirms the insert as `blocked_date`, not `holiday_date` —
    // reading only the latter meant the response was never used and the store
    // kept the optimistic request value instead of the date the server actually
    // recorded. Both are accepted; the request date is the last resort.
    const confirmedDate =
      typeof payload?.blocked_date === "string"
        ? payload.blocked_date
        : typeof payload?.holiday_date === "string"
          ? payload.holiday_date
          : null;
    const entry = confirmedDate
      ? { holiday_date: confirmedDate, reason: action.data?.reason }
      : action.data;
    yield put({
      type: `${ADD_HOLIDAY}_SUCCESS`,
      payload: entry,
      meta: action.meta ?? {},
    });
    if (action.onSuccess) yield call(action.onSuccess);
  } catch (error: any) {
    const err = error?.response?.data ?? error;
    yield put({ type: `${ADD_HOLIDAY}_FAILURE`, payload: err });
    if (action.onError) yield call(action.onError, err);
  }
}

export function* removeHolidayWorker(action: any): any {
  yield put({ type: `${REMOVE_HOLIDAY}_REQUEST` });
  try {
    // Returns the removed `blocked_date`, or null when the date was not a
    // holiday in the first place. Either way the calendar should end up without
    // it, so a null is treated as success rather than an error — it just means
    // someone else got there first.
    unwrap(yield call(RemoveHolidayApi, action.data));
    yield put({
      type: `${REMOVE_HOLIDAY}_SUCCESS`,
      meta: action.meta,
    });
    if (action.onSuccess) yield call(action.onSuccess);
  } catch (error: any) {
    const err = error?.response?.data ?? error;
    yield put({ type: `${REMOVE_HOLIDAY}_FAILURE`, payload: err });
    if (action.onError) yield call(action.onError, err);
  }
}

export function* getHolidaysByMonthWatcher() {
  yield takeLatest(GET_HOLIDAYS_BY_MONTH, getHolidaysByMonthWorker);
}

export function* addHolidayWatcher() {
  yield takeLatest(ADD_HOLIDAY, addHolidayWorker);
}

export function* removeHolidayWatcher() {
  yield takeLatest(REMOVE_HOLIDAY, removeHolidayWorker);
}
